<template>
    <div>
        <div ref="container" style="display: none" />
        <div class="lr-produto-modal-host" v-if="showProdutoModal">
            <shortcutComponent
                ref="shortcutProduto"
                :modalClass="'bg-modal'"
                :btOkName="'Salvar'"
                @selection-item="onConfirmarProduto"
                @hide-click="onHideProduto"
            >
                <div slot="component">
                    <produtoComponent
                        ref="produtoComponent"
                        :isModal="true"
                        :descricaoSugestao="nomeProdutoSugerido"
                        @save-ok="onProdutoSaveOk"
                    />
                </div>
            </shortcutComponent>
        </div>
    </div>
</template>

<script lang="ts">
import Vue from "vue";

import ProdutoEditComponent from "@/components/parent/crud/produto/edit";
import produtoComponent from "@/components/parent/crud/produto/edit.vue";
import ShortcutComponent from "@/components/parent/shortcut/shortcut";
import shortcutComponent from "@/components/parent/shortcut/shortcut.vue";
import ProdutoModel from "@/models/produto/produtoModel";
import Delay from "@/utils/common/delay";

// Interfaces locais — não importa tipos de src/react/ para evitar conflito
// com o namespace JSX global do Vue (shims-tsx.d.ts).
interface ReceitaDto {
    nomePaciente: string;
    cpf: string;
    textoCompleto: string;
    itens: unknown[];
}

interface ProdutoResultado {
    produtoId: number;
    descricao: string;
}

interface LerReceitaModalHandle {
    open(props: {
        onAplicar: (dto: ReceitaDto) => void;
        onCadastrarProduto: (nome: string) => Promise<ProdutoResultado | null>;
    }): void;
    close(): void;
    unmount(): void;
}

export default Vue.extend({
    name: "ReactMountLerReceita",

    components: {
        shortcutComponent,
        produtoComponent,
    },

    data(): {
        handle: LerReceitaModalHandle | null;
        alive: boolean;
        pendingOpen: boolean;
        showProdutoModal: boolean;
        nomeProdutoSugerido: string;
        resolveProduto: ((valor: ProdutoResultado | null) => void) | null;
        produtoSalvo: ProdutoModel | null;
    } {
        return {
            handle: null,
            alive: false,
            pendingOpen: false,
            showProdutoModal: false,
            nomeProdutoSugerido: "",
            resolveProduto: null,
            produtoSalvo: null,
        };
    },

    async mounted() {
        this.alive = true;
        // @ts-ignore — src/react/ é compilado pelo Babel; os tipos não estão
        // no tsconfig do Vue de propósito para evitar conflitos com JSX global.
        const { mountLerReceitaModal } = await import(
            /* webpackChunkName: "react-apps" */ "@/react/mount"
        );
        if (!this.alive) {
            return;
        }
        const container = this.$refs.container as HTMLElement | null;
        if (!container) {
            return;
        }
        this.handle = (mountLerReceitaModal as (el: HTMLElement) => LerReceitaModalHandle)(container);
        // Se pediram para abrir antes do import terminar, abre agora.
        if (this.pendingOpen) {
            this.pendingOpen = false;
            this.doOpen();
        }
    },

    beforeDestroy() {
        this.alive = false;
        this.handle?.unmount();
        this.handle = null;
    },

    methods: {
        // Chamado pelo vendaForm (this.$refs.lerReceitaComponent.open()).
        open() {
            if (this.handle) {
                this.doOpen();
            } else {
                this.pendingOpen = true;
            }
        },
        close() {
            this.handle?.close();
        },
        doOpen() {
            this.handle?.open({
                onAplicar: (dto: ReceitaDto) => {
                    this.$emit("aplicar-receita", dto);
                },
                onCadastrarProduto: (nome: string) => this.onCadastrarProduto(nome),
            });
        },

        /**
         * Ponte React→Vue: o modal React (aberto, montado) pede pra cadastrar um produto que não
         * existe no catálogo. Abre o form de Produto existente (crud/produto/edit.vue) dentro de
         * um ShortcutComponent (mesmo mecanismo de "Adicionar Novo" das combos, só que aqui
         * disparado diretamente, sem combo por trás) e devolve uma Promise resolvida no
         * save-ok (produto criado) ou no cancelamento (null) — o React aguarda sem fechar.
         */
        onCadastrarProduto(nome: string): Promise<ProdutoResultado | null> {
            return new Promise(resolve => {
                this.resolveProduto = resolve;
                this.nomeProdutoSugerido = nome;
                this.produtoSalvo = null;
                this.showProdutoModal = true;
                this.$nextTick(() => this.abrirModalProduto());
            });
        },

        async abrirModalProduto() {
            while (!this.$refs.produtoComponent || !this.$refs.shortcutProduto) {
                await Delay(5);
            }
            const produtoComponentRef = this.$refs.produtoComponent as ProdutoEditComponent;
            const shortcutRef = this.$refs.shortcutProduto as ShortcutComponent;
            await produtoComponentRef.load();
            shortcutRef.title = "Cadastrar Produto";
            shortcutRef.show();
        },

        onProdutoSaveOk(model: ProdutoModel) {
            this.produtoSalvo = model;
        },

        async onConfirmarProduto() {
            const produtoComponentRef = this.$refs.produtoComponent as ProdutoEditComponent;
            if (await produtoComponentRef.save()) {
                const resolve = this.resolveProduto;
                this.resolveProduto = null;
                this.showProdutoModal = false;
                resolve?.(
                    this.produtoSalvo
                        ? { produtoId: this.produtoSalvo.id, descricao: this.produtoSalvo.descricao }
                        : null,
                );
                (this.$refs.shortcutProduto as ShortcutComponent)?.hide();
            }
            // Se save() retornar false (validação falhou), o modal continua aberto mostrando os
            // erros — nada a fazer aqui.
        },

        onHideProduto() {
            const resolve = this.resolveProduto;
            this.resolveProduto = null;
            this.showProdutoModal = false;
            resolve?.(null);
        },
    },
});
</script>

<style scoped>
.lr-produto-modal-host {
    position: relative;
    isolation: isolate;
    z-index: 10010;
}
</style>
